import { Grid, IconButton } from "@material-ui/core";
import { DeleteForever } from "@material-ui/icons";
import { useEffect, useRef, useState } from "react";
import "../../AddNewProject/AddNewProject.css";
import { getEnvName } from "../../shared/constants/environment";

const PhasePanel = ({ phase, onChange, onDelete, canDelete, forceValidation, phaseIndex, onStreetInputFocus }) => {
  // Local state for form values
  const [formData, setFormData] = useState({ ...phase });
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const streetInputRef = useRef();
  
  // Update local state when phase prop changes (e.g., when address is selected from modal)
  useEffect(() => {
    setFormData({ ...phase });
    
    // Re-validate when the street field is updated externally
    if (phase.street && phase.street !== formData.street) {
      const newErrors = { ...errors };
      delete newErrors.street; // Clear street error since we now have a value
      setErrors(newErrors);
      setTouchedFields(prev => ({...prev, street: true}));
      
      // Notify parent with updated validation using the same logic as notifyParent
      const hasValidationErrors = Object.values(newErrors).some(Boolean);
      const hasRequiredFieldErrors = 
        !phase.phaseName?.trim() || 
        !phase.street?.trim() || 
        !phase.unitsPerPhase?.trim() ||
        !/^[1-9][0-9]*$/.test(phase.unitsPerPhase || '');
      
      const isValid = !hasValidationErrors && !hasRequiredFieldErrors;
      if (onChange) {
        onChange({ ...phase }, isValid);
      }
    }
  }, [phase]);

  // Initial validation check when component mounts
  useEffect(() => {
    const newErrors = {};

    if (!formData.phaseName?.trim()) {
      newErrors.phaseName = "Phase name is required";
    }    

    if (!formData.street?.trim()) {
      newErrors.street = "Street address is required";
    }

    if (!formData.unitsPerPhase?.trim()) {
      newErrors.unitsPerPhase = "Number of units is required";
    } else if (!/^[1-9][0-9]*$/.test(formData.unitsPerPhase)) {
      newErrors.unitsPerPhase = "Please enter a valid number greater than 0";
    }
    
    setErrors(newErrors);
  }, []); // Run only on mount

  // Notify parent of changes
  const notifyParent = (updatedData, updatedErrors) => {
    if (onChange) {
      // Check for validation errors AND required fields
      const hasValidationErrors = Object.values(updatedErrors).some(Boolean);
      const hasRequiredFieldErrors = 
        !updatedData.phaseName?.trim() || 
        !updatedData.street?.trim() || 
        !updatedData.unitsPerPhase?.trim() ||
        !/^[1-9][0-9]*$/.test(updatedData.unitsPerPhase || '');
      
      const isValid = !hasValidationErrors && !hasRequiredFieldErrors;
      onChange({ ...updatedData }, isValid);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    // console.log("handleInputChange", { field, value, touchedFields, errors, formData });

    if (value === formData[field]) {
      return;
    }
    
    const newFormData = {
      ...formData,
      [field]: value
    };

    // Perform immediate validation for this field only
    const newErrors = { ...errors };

    delete newErrors[field]; 
    
    if (field === "phaseName" && !value?.trim()) {
      newErrors.phaseName = "Phase name is required";
    } else if (field === "unitsPerPhase" && !value?.trim()) {
      newErrors.unitsPerPhase = "Number of units is required";
    } else if (field === "unitsPerPhase" && !/^[1-9][0-9]*$/.test(value)) {
      newErrors.unitsPerPhase = "Please enter a valid number greater than 0";
    } else if (field === "street" && !value?.trim()) {
      newErrors.street = "Street address is required";
    }

    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
    setErrors(newErrors);
    setFormData(newFormData);
    notifyParent(newFormData, newErrors);
  };

  // Helper function to determine if we should show validation errors for a field
  const shouldShowError = (fieldName) => !!((touchedFields[fieldName] || forceValidation) && errors[fieldName])

  return (
    <div
      className={"phase-panel show-validation-" + !!forceValidation}
      style={{
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "space-between",
      }}
    >
      <Grid container spacing={2} style={{ border: "1px solid #ccc", padding: 10, margin: 10 }}>
        <Grid item container spacing={2} xs={12} sm={12} md={5}>
          <Grid item xs={12} sm={12} md={12}>
            <div>
              Phase Name<span style={{ color: "red" }}>*</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <input
              type="text"
              value={formData.phaseName}
              className={"form-control " + (shouldShowError("phaseName") ? "is-invalid" : "")}
              maxLength="300"
              style={{ marginBottom: shouldShowError("phaseName") ? 0 : "24px" }}
              onChange={(event) => {
                handleInputChange("phaseName", event.target.value);
              }}
            />
            {shouldShowError("phaseName") && (
              <div style={{color: "red"}}>{errors.phaseName}</div>
            )}
          </Grid>
        </Grid>
        <Grid item container spacing={2} xs={12} sm={12} md={7}>
          <Grid item xs={12} sm={12} md={12}>
            <div>
              Street Address<span style={{ color: "red" }}>*</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <input
              type="text"
              id={`street-${phaseIndex}`}
              ref={streetInputRef}
              value={formData.street}
              className={"form-control " + (shouldShowError("street") ? "is-invalid" : "")}
              style={{ 
                marginBottom: shouldShowError("street") ? 0 : "24px",
                cursor: "pointer"
              }}
              placeholder="Click to select street address..."
              onChange={() => {}}
              onFocus={() => {
                if (onStreetInputFocus) {
                  onStreetInputFocus();
                  streetInputRef.current.blur();
                }
              }}
              onClick={() => {
                if (onStreetInputFocus) {
                  onStreetInputFocus();
                  streetInputRef.current.blur();
                }
              }}
            />
            {shouldShowError("street") && (
              <div style={{color: "red"}}>{errors.street}</div>
            )}
          </Grid>
        </Grid>

        <Grid item container spacing={2} xs={6} sm={4} md={4}>
          <Grid item xs={12} sm={12} md={12}>
            <div>Building Number</div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <input
              type="text"
              value={formData.buildingNumber}
              className="form-control"
              maxLength="100"
              style={{ marginBottom: "24px" }}
              onChange={(event) => {
                handleInputChange("buildingNumber", event.target.value);
              }}
            />
          </Grid>
        </Grid>

        <Grid item container spacing={2} xs={6} sm={4} md={4}>
          <Grid item xs={12} sm={12} md={12}>
            <div>Unit Number</div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <input
              type="text"
              value={formData.unitNumber}
              className="form-control"
              maxLength="100"
              style={{ marginBottom: "24px" }}
              onChange={(event) => {
                handleInputChange("unitNumber", event.target.value);
              }}
            />
          </Grid>
        </Grid>
        <Grid item container spacing={2} xs={6} sm={4} md={4}>
          <Grid item xs={12} sm={12} md={12}>
            <div>Number of Units in Phase<span style={{ color: "red" }}>*</span></div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <input
              type="number"
              value={formData.unitsPerPhase}
              className={"form-control" + (shouldShowError("unitsPerPhase") ? " is-invalid" : "")}
              min={1}
              style={{ marginBottom: shouldShowError("unitsPerPhase") ? 0 : "24px" }}
              onChange={(event) => {
                handleInputChange("unitsPerPhase", event.target.value);
              }}
            />
            {shouldShowError("unitsPerPhase") && (
              <div style={{color: "red"}}>{errors.unitsPerPhase}</div>
            )}
          </Grid>
        </Grid>
      </Grid>
      <IconButton onClick={onDelete} disabled={!canDelete}>
        <DeleteForever style={{ color: canDelete ? "#c00" : "#ccc" }} />
      </IconButton>
    </div>
  );
};

export default PhasePanel;